import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, timer } from 'rxjs';
import { map, switchMap, takeWhile } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { IFileInfo } from '../main/models/file-info';
import { SpatialDetection, SpatialOmicsService } from './spatial-omics.service';

/** Where a dataset's omics preparation (a CONVERT job on the server) stands. */
export type OmicsPrepareState = 'ready' | 'submitting' | 'running' | 'required' | 'failed';

/** The optional parts a dataset can have, and which it has (absent: not one this dataset can have). */
export interface OmicsBuilt {
  /** The all-gene transcript pyramid. */
  transcripts?: boolean;
  /** The pyramid's per-gene levels. */
  geneLevels?: boolean;
  /** A zipped Xenium bundle's extracted morphology images. */
  morphology?: boolean;
  /** A Xenium bundle's marker genes, scored per clustering when it is prepared. */
  markers?: boolean;
  /** How many levels the transcript pyramid has (0 without one). */
  levels?: number;
  /** How many levels its per-gene levels have (0 without them). */
  geneLevelCount?: number;
}

/** What jit-service's `/spatial/{id}/prepare` says. */
export interface OmicsPrepareStatus {
  state: OmicsPrepareState;
  error?: string | null;
  /** The convert job, once submitted; it is also in the process status list. */
  workflowId?: string | null;
  /** Which optional parts exist; null when the dataset has none to build. */
  built?: OmicsBuilt | null;
}

/** What the convert job builds besides the dataset (jit-api's OmicsOptions). */
export interface OmicsOptions {
  transcripts: boolean;
  geneLevels: boolean;
  /** Pyramid levels, 1 to 8, each twice as coarse as the one below, from 1.95 µm. */
  levels: number;
  morphology: boolean;
  /** Score a Xenium bundle's marker genes of its clusterings, so "Add marker genes" is immediate. */
  markers: boolean;
}

/** The server's defaults: everything, seven levels. */
export const DEFAULT_OMICS_OPTIONS: OmicsOptions = {
  transcripts: true, geneLevels: true, levels: 7, morphology: true, markers: true,
};

/**
 * The dataset kinds an omics convert prepares: AnnData, which jit-service reads only once converted,
 * and 10x Xenium bundles, which it reads as they are but which gain a transcript pyramid and (a zip)
 * extracted morphology images.
 */
const PREPARABLE_KINDS = ['h5ad', 'spatialdata', 'xenium-zip', 'xenium-folder'];

/**
 * Prepares omics datasets for Spatial omics: jit-service submits the convert job (the omics
 * pipeline) and reports its state, so the UI never converts or chooses the output location itself.
 */
@Injectable({ providedIn: 'root' })
export class OmicsPrepareService {
  /** How often a submitted preparation is checked. */
  static readonly POLL_MS = 2000;
  private readonly url = environment.slideCropServer;

  constructor(private http: HttpClient, private spatial: SpatialOmicsService) {}

  /** Whether {@code kind} (from `/spatial/detect`) is a dataset this service can prepare. */
  static isPreparable(kind: string | undefined): boolean {
    return !!kind && PREPARABLE_KINDS.includes(kind);
  }

  /** Whether {@code options} ask for a part {@code built} says the dataset lacks. */
  static missing(built: OmicsBuilt | null | undefined, options: OmicsOptions): boolean {
    if (!built) return false;
    if (options.transcripts && built.transcripts === false) return true;
    // More levels than the pyramid has: it is rebuilt with them.
    if (options.transcripts && built.levels !== undefined && built.levels < options.levels) return true;
    if (options.transcripts && options.geneLevels && built.geneLevels === false) return true;
    // Per-gene levels short of the request (an upgrade made without them): the job completes them.
    if (options.transcripts && options.geneLevels && built.geneLevelCount !== undefined
      && built.geneLevelCount < options.levels) return true;
    if (options.markers && built.markers === false) return true;
    return options.morphology && built.morphology === false;
  }

  detect(file: IFileInfo): Promise<SpatialDetection> {
    return this.spatial.detect(file);
  }

  /** Submit the dataset's convert job: a no-op when it is ready and has what {@code options} ask for. */
  prepare(id: string, options?: OmicsOptions): Observable<OmicsPrepareStatus> {
    return this.http.post<OmicsPrepareStatus>(this.endpoint(id), options ?? null);
  }


  status(id: string): Observable<OmicsPrepareStatus> {
    return this.http.get<OmicsPrepareStatus>(this.endpoint(id));
  }

  /** The status every {@link POLL_MS}, ending (after emitting it) at ready or failed. */
  watch(id: string): Observable<OmicsPrepareStatus> {
    return timer(0, OmicsPrepareService.POLL_MS).pipe(
      switchMap(() => this.status(id)),
      takeWhile((s) => s.state !== 'ready' && s.state !== 'failed', true),
      map((s) => s));
  }

  private endpoint(id: string): string {
    return `${this.url}spatial/${encodeURIComponent(id)}/prepare`;
  }
}
